import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit as fbLimit,
  getDocs,
  getFirestore,
} from 'firebase/firestore';
import { app } from '../../firebase/firebaseConfig';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowLeft, RefreshCw, AlertTriangle, UserMinus, Clock } from 'lucide-react';
import SessionEventsView from './SessionEventsView';
import SessionStatsView from './SessionStatsView';

// Full-screen detail view for one device's igloo_exited event:
// fetches the per-device exit doc and tabs the discrete events
// (session_diagnostics) and per-60s metric stats (session_summary).
//
// Props:
//   familyId        — the family that left
//   familyName      — display name
//   eventTimestamp  — leave event time (ms)
//   iglooId         — to disambiguate when a family has multiple recent exits
//   iglooName       — for header context
//   onBack          — called when the user clicks the back button
function SessionDetailPanel({ familyId, familyName, eventTimestamp, iglooId, iglooName, onBack }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exitEvent, setExitEvent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchExit() {
      setLoading(true);
      setError(null);
      setExitEvent(null);
      try {
        const db = getFirestore(app);
        const eventsRef = collection(db, 'deviceEvents', familyId, 'events');
        const start = eventTimestamp - 2 * 60 * 1000;
        const end = eventTimestamp + 2 * 60 * 1000;
        const q = query(
          eventsRef,
          where('timestamp', '>=', start),
          where('timestamp', '<=', end),
          orderBy('timestamp', 'asc'),
          fbLimit(20)
        );
        const snap = await getDocs(q);
        const matches = snap.docs
          .map((d) => d.data())
          .filter((e) => e.event_type === 'igloo_exited' && (!iglooId || e.igloo_id === iglooId));

        if (cancelled) return;

        if (matches.length === 0) {
          setError('No igloo_exited event found for this leave.');
        } else {
          matches.sort(
            (a, b) =>
              Math.abs(a.timestamp - eventTimestamp) - Math.abs(b.timestamp - eventTimestamp)
          );
          setExitEvent(matches[0]);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchExit();
    return () => {
      cancelled = true;
    };
  }, [familyId, eventTimestamp, iglooId]);

  const diagnosticsCount = exitEvent?.session_diagnostics?.length || 0;
  const summaryKeys = exitEvent?.session_summary
    ? Object.keys(exitEvent.session_summary).filter((k) => k !== 'timestampMs').length
    : 0;
  const exitDate = new Date(eventTimestamp);
  const exitLabel = exitDate.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <Card className="h-full">
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack} className="h-8 w-8 p-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <CardTitle className="flex items-center gap-2 text-lg">
              <UserMinus className="w-5 h-5 text-gray-500" />
              <span>{familyName}</span>
              <span className="text-sm font-normal text-muted-foreground">left</span>
            </CardTitle>
            <Badge variant="secondary">Exit details</Badge>
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{exitLabel}</span>
            </div>
            {iglooName && (
              <span className="hidden md:inline">in {iglooName}</span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            Loading exit event…
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 py-10 text-sm text-destructive">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        ) : (
          <Tabs defaultValue="events">
            <TabsList>
              <TabsTrigger value="events">
                Session events
                <span className="ml-2 text-xs text-muted-foreground">{diagnosticsCount}</span>
              </TabsTrigger>
              <TabsTrigger value="stats">
                Session stats
                <span className="ml-2 text-xs text-muted-foreground">{summaryKeys}</span>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="events" className="mt-3">
              <SessionEventsView diagnostics={exitEvent.session_diagnostics} />
            </TabsContent>
            <TabsContent value="stats" className="mt-3">
              <SessionStatsView summary={exitEvent.session_summary} />
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}

export default SessionDetailPanel;
